import React from 'react';
import Skeleton from 'react-loading-skeleton';
import type { SelectOption } from 'react-select-me';
import SelectMe from 'react-select-me';

import type { GeocodingFeature } from '../types';

type PropertyFieldKey = 'typeDemande' | 'typeBien' | 'adresseBien';

interface CoproOption {
  value: number;
  label: string;
  [key: string]: number | string | unknown;
}

interface PropertyInfoSectionProps {
	isEdit: boolean;
	fields: Record<PropertyFieldKey, string>;
	setField: (key: PropertyFieldKey, value: string) => void;
	errors: Record<string, string>;
	loadingCopros: boolean;
	errorCopros: string | null;
	coproOptions: CoproOption[];
	currentCoproId: number | null;
	setCurrentCoproId: (id: number | null) => void;
	addressSuggestions: Array<{ label: string; value: GeocodingFeature }>;
	onAdresseChange: (value: string) => void;
	onAddressSuggestionClick: (feature: GeocodingFeature) => void;
}

function PropertyInfoSection({
	isEdit,
	fields,
	setField,
	errors,
	loadingCopros,
	errorCopros,
	coproOptions,
	currentCoproId,
	setCurrentCoproId,
	addressSuggestions,
	onAdresseChange,
	onAddressSuggestionClick,
}: PropertyInfoSectionProps) {
	return (
		<>
			<div className="my-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
				<div className="font-semibold text-blue-900"><span role="img" aria-label="house">🏠</span> Informations du Bien</div>
			</div>
			<div className="grid gap-4 md:grid-cols-2">
				<div>
					<label className="text-sm font-semibold text-slate-700"><span role="img" aria-label="target">🎯</span> Type de demande *</label>
					<select value={fields.typeDemande} onChange={(e) => setField('typeDemande', e.target.value)} className="w-full rounded-lg border-2 px-3 py-2">
						<option value="">Sélectionnez...</option>

						<option value="vente"><span role="img" aria-label="house">🏠</span> Vente (Estimation)</option>
						<option value="location"><span role="img" aria-label="emoji">🔑</span> Location (Évaluation loyer)</option>
					</select>
					{errors.typeDemande && <div className="mt-1 text-xs text-red-600">{errors.typeDemande}</div>}
				</div>
				<div>
					<label className="text-sm font-semibold text-slate-700"><span role="img" aria-label="office building">🏢</span> Type de bien *</label>
					<select value={fields.typeBien} onChange={(e) => setField('typeBien', e.target.value)} className="w-full rounded-lg border-2 px-3 py-2">
						<option value="">Sélectionnez...</option>
						<option value="appartement">Appartement</option>
						<option value="maison">Maison individuelle</option>
						<option value="studio">Studio</option>
						<option value="duplex">Duplex</option>
						<option value="loft">Loft</option>
						<option value="local">Local commercial</option>
						<option value="terrain">Terrain</option>
						<option value="garage">Garage/Parking</option>
					</select>
					{errors.typeBien && <div className="mt-1 text-xs text-red-600">{errors.typeBien}</div>}
				</div>
				{!isEdit && (
					<div className="md:col-span-2">
						<label className="text-sm font-semibold text-slate-700"><span role="img" aria-label="office building">🏢</span> Sélection de la copropriété *</label>
						<div className="mt-1">
							{loadingCopros ? (
								<Skeleton height={38} />
							) : (
							<SelectMe
									options={coproOptions as Array<SelectOption<number>>}
									value={currentCoproId ? (coproOptions.find((o) => o.value === currentCoproId) as SelectOption<number> | undefined) : undefined}
									onChange={(opt: SelectOption<number>[]) => setCurrentCoproId(opt[0]?.value ?? null)}
									placeholder="Recherchez une copropriété..."
									isClearable
								/>
							)}
							{errorCopros && <div className="mt-1 text-xs text-red-600">{errorCopros}</div>}
						</div>
					</div>
				)}
				<div className="md:col-span-2">
					<label className="text-sm font-semibold text-slate-700"><span role="img" aria-label="emoji">📍</span> Adresse complète *</label>
					<div className="relative">
						<input
							value={fields.adresseBien}
							onChange={(e) => onAdresseChange(e.target.value)}
							className="w-full rounded-lg border-2 px-3 py-2"
							placeholder="Ex: 15 rue des Lilas, 75015 Paris"
							autoComplete="off"
						/>
						{addressSuggestions.length > 0 && (
							<ul className="absolute z-[999] mt-1 max-h-56 w-full overflow-auto rounded border border-gray-300 bg-white shadow">
								{addressSuggestions.map((sugg, idx) => (
									<li
										key={`addr-sugg-${idx}`}
										className="cursor-pointer p-2 text-sm hover:bg-gray-100"
									onClick={() => onAddressSuggestionClick(sugg.value)}
									>
										{sugg.label}
									</li>
								))}
							</ul>
						)}
					</div>
					{errors.adresseBien && <div className="mt-1 text-xs text-red-600">{errors.adresseBien}</div>}
				</div>
			</div>
		</>
	);
}

export default PropertyInfoSection;


